import type { GroupAnalysisSpanData } from '@/generated/types/navigator/types.gen';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { clearItem, type SelectedItem } from '@/features/analysisMapSlice';
import { updateWorklogStatus } from '@/features/worklogSlice';
import { useFetchNetworkGroupItemQuery } from '@/features/analysisApi';
import { LayerType } from '@/views/map/layers/types';
import { useSpanDetailContent } from '@/views/map/popover/SpanDetailContent';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useCurrentRootPath } from '@/views/useUrlSync';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import ListPane from '@/views/list/ListPane';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { X } from 'react-feather';
import { IconButton } from '@/components';
import { useMantineTheme } from '@mantine/core';
import classes from './SpanDetailSidebar.module.css';

interface SpanDetailSidebarProps {
  selectedItem: SelectedItem;
}

export const SpanDetailSidebar = ({ selectedItem }: SpanDetailSidebarProps) => {
  const dispatch = useAppDispatch();
  const navigateWithSearch = useNavigateWithSearch();
  const rootPath = useCurrentRootPath();
  const theme = useMantineTheme();
  const { selectedNetworkGroup, spanNetworkGroup } = useAppSelector(
    (s) => s.analysisMap
  );

  const itemProps = selectedItem?.object?.properties || {};

  const {
    data: groupItemData,
    refetch: groupItemRefetch,
    isUninitialized,
  } = useFetchNetworkGroupItemQuery(
    {
      item: encodeURIComponent(itemProps.group ?? ''),
      group_id: selectedNetworkGroup?.id,
    },
    {
      skip: selectedItem.layer?.id !== LayerType[LayerType.scan],
    }
  );

  useEffect(() => {
    if (
      selectedItem.layer?.id === LayerType[LayerType.scan] &&
      !isUninitialized
    ) {
      groupItemRefetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  useDocumentTitle({
    title: selectedItem.object?.properties?.displayName ?? undefined,
    paths: ['*'],
  });

  const spanData = groupItemData?.properties;

  const handleClose = () => {
    dispatch(clearItem());
    // Reset worklog status when closing — this prevents stale worklog UI
    // from persisting if the user navigated here from a worklog flow.
    // TODO: Consider moving this coupling into a shared close/cleanup action.
    dispatch(updateWorklogStatus(false));
    navigateWithSearch(rootPath);
  };

  const isReady =
    spanData &&
    (groupItemData?.properties?.networkGroupId === spanNetworkGroup?.id ||
      selectedNetworkGroup?.id === spanNetworkGroup?.id);

  if (!isReady) {
    return (
      <ListPane>
        <div className={classes.loading} data-testid="span-detail-sidebar" />
      </ListPane>
    );
  }

  return (
    // Cap the sidebar at 1080px so it doesn't stretch the full viewport on tall screens.
    <ListPane listWrapperStyles={{ maxHeight: 1080 }}>
      <ErrorBoundary>
        <SpanDetailSidebarContent
          a={spanData}
          onClose={handleClose}
          theme={theme}
        />
      </ErrorBoundary>
    </ListPane>
  );
};

const SpanDetailSidebarContent = ({
  a,
  onClose,
  theme,
}: {
  a: GroupAnalysisSpanData;
  onClose: () => void;
  theme: ReturnType<typeof useMantineTheme>;
}) => {
  const { getHeader, tabs } = useSpanDetailContent(a);

  return (
    <div className={classes.container} data-testid="span-detail-sidebar">
      <div className={classes.closeButton}>
        <IconButton
          Component={X}
          onClick={onClose}
          color={theme.other.mainColor.foreground.secondary}
          iconSize={20}
        />
      </div>
      <div className={classes.header}>{getHeader()}</div>
      <div className={classes.tabs}>{tabs}</div>
    </div>
  );
};
